import { Head, Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    UsersIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    MapPinIcon,
    BuildingOfficeIcon,
    CalendarDaysIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

export default function Index({ employees, filters }) {
    const { flash } = usePage().props;
    const [search, setSearch] = useState(filters?.search || '');
    const [status, setStatus] = useState(filters?.status || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Leave modal state
    const [leaveModal, setLeaveModal] = useState(null); // employee object
    const [leaveStart, setLeaveStart] = useState('');
    const [leaveEnd, setLeaveEnd] = useState('');
    const [leaveReason, setLeaveReason] = useState('');
    const [leaveProcessing, setLeaveProcessing] = useState(false);

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/employees', { search, status }, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        setStatus('');
        router.get('/employees');
    };

    const handleDelete = (id) => {
        router.delete(`/employees/${id}`, {
            preserveScroll: true,
            onSuccess: () => setDeleteConfirm(null),
        });
    };

    const openLeaveModal = (employee) => {
        setLeaveModal(employee);
        setLeaveStart('');
        setLeaveEnd('');
        setLeaveReason('');
    };

    const submitLeave = () => {
        if (!leaveStart || !leaveEnd) return;
        setLeaveProcessing(true);
        router.post(`/employees/${leaveModal.id}/leaves`, {
            start_date: leaveStart,
            end_date: leaveEnd,
            reason: leaveReason,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setLeaveModal(null);
                setLeaveProcessing(false);
            },
            onError: () => setLeaveProcessing(false),
        });
    };

    const cancelLeave = (employeeId, leaveId) => {
        router.delete(`/employees/${employeeId}/leaves/${leaveId}`, {
            preserveScroll: true,
        });
    };

    return (
        <AppLayout>
            <Head title="Employees" />
            <PageHeader
                title="Employees"
                subtitle="Manage employee records for timesheet tracking"
                actions={
                    <Button href="/employees/create" size="sm">
                        <PlusIcon className="w-4 h-4" /> Add Employee
                    </Button>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="error" className="mb-4">{flash.error}</Alert>}

            {/* Filter Bar */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by name or code..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                            />
                        </div>
                        <FormSelect value={status} onChange={e => setStatus(e.target.value)} className="w-full sm:w-40">
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </FormSelect>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">
                                <FunnelIcon className="w-4 h-4" /> Apply
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                Reset
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Employees Table */}
            <Card>
                <Table minWidth="800px">
                    <thead>
                        <tr>
                            <Th>#</Th>
                            <Th>Employee Code</Th>
                            <Th>Full Name</Th>
                            <Th>Work Location</Th>
                            <Th>Department</Th>
                            <Th>Leave</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {employees?.map?.((employee, idx) => (
                            <Tr key={employee.id}>
                                <Td className="text-slate-400 text-xs">{idx + 1}</Td>
                                <Td>
                                    <Badge variant="neutral" className="font-mono">
                                        {employee.employee_code}
                                    </Badge>
                                </Td>
                                <Td className="font-medium text-slate-800 whitespace-nowrap">
                                    {employee.full_name}
                                </Td>
                                <Td className="whitespace-nowrap">
                                    {employee.work_location_name ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                                            <MapPinIcon className="w-3.5 h-3.5 text-emerald-600" />
                                            {employee.work_location_name}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                    )}
                                </Td>
                                <Td className="whitespace-nowrap">
                                    {employee.department_name ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                                            <BuildingOfficeIcon className="w-3.5 h-3.5 text-blue-600" />
                                            {employee.department_name}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                    )}
                                </Td>
                                <Td>
                                    {employee.current_leave ? (
                                        <div className="flex items-center gap-1">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-orange-100 text-orange-700 rounded-full">
                                                <CalendarDaysIcon className="w-3 h-3" />
                                                On Leave
                                            </span>
                                            <button
                                                onClick={() => cancelLeave(employee.id, employee.current_leave.id)}
                                                className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                                title="Cancel leave"
                                            >
                                                <XMarkIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : employee.upcoming_leave ? (
                                        <div className="flex items-center gap-1">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-600 rounded-full">
                                                <CalendarDaysIcon className="w-3 h-3" />
                                                {employee.upcoming_leave.start_date}
                                            </span>
                                            <button
                                                onClick={() => cancelLeave(employee.id, employee.upcoming_leave.id)}
                                                className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                                title="Cancel leave"
                                            >
                                                <XMarkIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                    )}
                                </Td>
                                <Td>
                                    <Badge variant={employee.is_active ? 'success' : 'neutral'}>
                                        {employee.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        <Link
                                            href={`/employees/${employee.id}/edit`}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition"
                                        >
                                            <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                                        </Link>
                                        <button
                                            onClick={() => openLeaveModal(employee)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition"
                                        >
                                            <CalendarDaysIcon className="w-3.5 h-3.5" /> Leave
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(employee.id)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition"
                                        >
                                            <TrashIcon className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!employees || employees.length === 0) && (
                    <EmptyState
                        icon={UsersIcon}
                        title="No employees found"
                        description="Add your first employee to start tracking timesheets."
                        action={
                            <Button href="/employees/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> Add Employee
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Employee"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete this employee? This action cannot be undone.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
                </ModalFooter>
            </Modal>

            {/* Leave Modal */}
            <Modal
                show={!!leaveModal}
                onClose={() => setLeaveModal(null)}
                title="Mark as Leave"
                iconBg="bg-orange-100"
                iconColor="text-orange-600"
                icon={<CalendarDaysIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    {leaveModal && (
                        <div className="space-y-4">
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                <p className="text-sm font-medium text-slate-800">{leaveModal.full_name}</p>
                                <p className="text-xs text-slate-400">{leaveModal.employee_code}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={leaveStart}
                                        onChange={e => setLeaveStart(e.target.value)}
                                        className="w-full rounded-xl border border-slate-200 text-sm text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 px-4 py-2.5"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={leaveEnd}
                                        onChange={e => setLeaveEnd(e.target.value)}
                                        min={leaveStart}
                                        className="w-full rounded-xl border border-slate-200 text-sm text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 px-4 py-2.5"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
                                <input
                                    type="text"
                                    value={leaveReason}
                                    onChange={e => setLeaveReason(e.target.value)}
                                    placeholder="e.g. Annual leave, Sick leave..."
                                    className="w-full rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 px-4 py-2.5"
                                />
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setLeaveModal(null)}>Cancel</Button>
                    <Button onClick={submitLeave} disabled={leaveProcessing || !leaveStart || !leaveEnd}>
                        {leaveProcessing ? 'Saving...' : 'Confirm Leave'}
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
